import { createAsyncThunk } from '@reduxjs/toolkit';

import { AdminApiPaths } from '../../../api/APIPaths';
import APIDataProvider, {
  TApiPaginationResponse,
  TApiResponse,
} from '../../../api/ApiDataProvider';
import { ICourse, TCourseStatus, TCourseMode, TCourseType } from '.';

export type TGetCourseAsAdmin = {
  id: string;
  isSubSectionsAllowed?: boolean;
};

export type TGetCoursesForAdmin = {
  limit: number;
  offset: number;
  searchText?: string;
  curriculum?: string;
  subject?: string;
  grade?: string;
  studentId?: string;
  tutorId?: string;
  status?: TCourseStatus;
  mode?: string;
  type?: string;
  includeTutorRate?: boolean;
  includeLastCoursePurchaseRate?: boolean;
};

export type TCreateCourseAsAdmin = {
  title: string;
  type: string;
  description?: string;
  isOneToOne?: boolean;
  templateId?: string;
};

export type TUpdateCourseAsAdmin = {
  _id: string;
  title?: string;
  subTitle?: string;
  description?: string;
  curriculum?: string;
  subject?: string;
  grade?: string;
  pricingTemplate?: string;
  permissionTemplate?: string;
  noOfClasses?: number;
  duration?: number;
  startDate?: string;
  endDate?: string;
  classTime?: string;
  classDays?: string[];
  status?: TCourseStatus;
  pricingDetails?: Array<string>;
  visibility?: boolean;
  selfEnrolement?: boolean;
  isStep1Completed?: boolean;
  isStep2Completed?: boolean;
  isStep3Completed?: boolean;
  isStep4Completed?: boolean;
  groupTutorRateCourseType?: string;
  chat?: {
    isChatEnabled: boolean;
    isEveryoneCanChat: boolean;
  };
  isLessonBookingEnabled?: boolean;
};

export type TDeleteCourseSectionAsAdmin = {
  courseId: string;
  sectionId: string;
};

export type TGetCourseListForDropDownAsAdmin = {
  offset: number;
  limit: number;
  searchText?: string;
  studentId?: string;
  mode?: TCourseMode;
  type?: TCourseType;
  includeTutorRate?: boolean;
  studentPricingTypes?: Array<string>;
};

const apiDataProvider = new APIDataProvider();

const getCoursesForAdminModule = async (data: TGetCoursesForAdmin) => {
  let path = `${AdminApiPaths.courses}?limit=${data.limit}&offset=${data.offset}`;

  if (data.searchText) {
    path += `&searchText=${encodeURIComponent(data.searchText)}`;
  }

  if (data.curriculum) {
    path += `&curriculum=${data.curriculum}`;
  }

  if (data.subject) {
    path += `&subject=${data.subject}`;
  }

  if (data.grade) {
    path += `&grade=${data.grade}`;
  }

  if (data.studentId) {
    path += `&studentId=${data.studentId}`;
  }

  if (data.tutorId) {
    path += `&tutorId=${data.tutorId}`;
  }

  if (data.status) {
    path += `&status=${data.status}`;
  }

  if (data.mode) {
    path += `&mode=${data.mode}`;
  }

  if (data.type) {
    path += `&type=${data.type}`;
  }

  if (data.includeTutorRate) {
    path += `&includeTutorRate=1`;
  }

  if (data.includeLastCoursePurchaseRate) {
    path += `&includeLastCoursePurchaseRate=1`;
  }

  const response = await apiDataProvider.get<
    TApiResponse<TApiPaginationResponse<ICourse>>
  >(path);

  return response.data;
};

const createCourseAsAdminModule = async (data: TCreateCourseAsAdmin) => {
  const path = `${AdminApiPaths.courses}`;
  const response = await apiDataProvider.post<TApiResponse<ICourse>>(
    path,
    data
  );

  return response.data;
};

export const getCoursesForAdmin = createAsyncThunk(
  '/admin/courses/list',
  getCoursesForAdminModule
);

export const loadMoreCoursesForAdmin = createAsyncThunk(
  '/admin/courses/loadMore',
  getCoursesForAdminModule
);

export const createCourseAsAdmin = createAsyncThunk(
  '/admin/courses/create',
  createCourseAsAdminModule
);

export const createCourseByTemplateAsAdmin = createAsyncThunk(
  '/admin/courses/template/create',
  createCourseAsAdminModule
);

export const getCourseByIdForAdmin = createAsyncThunk(
  '/admin/courses/getOne',
  async (data: TGetCourseAsAdmin) => {
    let path = `${AdminApiPaths.courses}/${data.id}`;

    if (data.isSubSectionsAllowed) {
      path += `?subSections=1`;
    }

    const response = await apiDataProvider.get<TApiResponse<ICourse>>(path);

    return response.data;
  }
);

export const updateCourseAsAdmin = createAsyncThunk(
  '/admin/courses/updateOne',
  async ({ _id, ...data }: TUpdateCourseAsAdmin) => {
    const path = `${AdminApiPaths.courses}/${_id}`;
    const response = await apiDataProvider.put<TApiResponse<ICourse>>(
      path,
      data
    );

    return response.data;
  }
);

export const deleteCourseAsAdmin = createAsyncThunk(
  '/admin/courses/deleteOne',
  async (id: string) => {
    const path = `${AdminApiPaths.courses}/${id}`;
    const response = await apiDataProvider.delete<TApiResponse<ICourse>>(path);

    return response.data;
  }
);

export const getCourseListForDropDownAsAdmin = createAsyncThunk(
  '/admin/courses/list-for-dropdown',
  async ({
    limit,
    offset,
    searchText,
    studentId,
    mode: type,
    includeTutorRate,
    studentPricingTypes,
  }: TGetCourseListForDropDownAsAdmin) => {
    let path = `${AdminApiPaths.courses}?limit=${limit}&offset=${offset}`;

    if (searchText) {
      path += `&searchText=${searchText}`;
    }

    if (studentId) {
      path += `&studentId=${studentId}`;
    }

    if (type) {
      path += `&type=${type}`;
    }

    if (includeTutorRate) {
      path += '&includeTutorRate=1';
    }

    if (studentPricingTypes?.length) {
      path += `&studentPricingTypes=${studentPricingTypes.join(',')}`;
    }

    const response = await apiDataProvider.get<
      TApiResponse<TApiPaginationResponse<ICourse>>
    >(path);

    return response.data;
  }
);
